import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import AdaptiveLayout from './components/AdaptiveLayout';
import AdminRoute from './components/AdminRoute';
import CookieBanner from './components/CookieBanner';
import ErrorBoundary from './components/ErrorBoundary';
import { useAuth } from './hooks/useAuth';
import { useBundleOptimization } from './hooks/usePerformance';
import { NotificationProvider } from './contexts/NotificationContext';

// Lazy loading con preload per route critiche
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const EmailVerificationPage = lazy(() => import('./pages/EmailVerificationPage'));

// Route critiche - preload immediato
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const DocumentVerificationPage = lazy(() => import('./pages/DocumentVerificationPage'));
const CruscottoPremium = lazy(() => import('./pages/CruscottoPremium'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));

// Route secondarie - preload on hover/interaction
const LoginFinalizePage = lazy(() => import('./pages/LoginFinalizePage'));
const ConsentsPage = lazy(() => import('./pages/ConsentsPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const RentalApplications = lazy(() => import('./pages/RentalApplications'));
const TenantSearch = lazy(() => import('./pages/TenantSearch'));
const LandlordSearch = lazy(() => import('./pages/LandlordSearch'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const AgencyLanding = lazy(() => import('./pages/AgencyLanding'));

// Nuove pagine con utilities moderne
const LandlordDashboard = lazy(() => import('./pages/LandlordDashboard'));
const PropertyFormPage = lazy(() => import('./pages/PropertyFormPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const PropertyDetailPage = lazy(() => import('./pages/PropertyDetailPage'));
const ApplicationDetailPage = lazy(() => import('./pages/ApplicationDetailPage'));
const ReviewsPage = lazy(() => import('./pages/ReviewsPage'));
const ReviewFormPage = lazy(() => import('./pages/ReviewFormPage'));
const Contracts = lazy(() => import('./pages/Contracts'));
const RankingKpiCenter = lazy(() => import('./pages/RankingKpiCenter'));
const ContractDetailPage = lazy(() => import('./pages/ContractDetailPage'));
const ContractDocumentsPage = lazy(() => import('./pages/ContractDocumentsPage'));
const PaymentHistoryPage = lazy(() => import('./pages/PaymentHistoryPage'));
const NewMaintenanceRequestPage = lazy(() => import('./pages/NewMaintenanceRequestPage'));

// Loading fallback component ottimizzato
const LoadingSpinner: React.FC = React.memo(() => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    fontSize: '1.2rem'
  }}>
    🔄 Caricamento...
  </div>
));

// Componente per preload intelligente
const RoutePreloader: React.FC<{ children: React.ReactNode }> = React.memo(({ children }) => {
  const location = useLocation();
  const { preloadComponent } = useBundleOptimization();

  useEffect(() => {
    // Preload route critiche basate sulla posizione attuale
    const preloadCriticalRoutes = () => {
      if (location.pathname === '/') {
        // Dalla home, preload login e register
        preloadComponent(() => import('./pages/LoginPage'));
        preloadComponent(() => import('./pages/RegisterPage'));
      } else if (location.pathname.startsWith('/cruscotto')) {
        // Dal cruscotto, preload componenti comuni
        preloadComponent(() => import('./pages/ProfilePage'));
        preloadComponent(() => import('./pages/DocumentsPage'));
      }
    };

    // Preload dopo un breve delay per non bloccare il rendering iniziale
    const timer = setTimeout(preloadCriticalRoutes, 100);
    return () => clearTimeout(timer);
  }, [location.pathname, preloadComponent]);

  return <>{children}</>;
});

import Home from './pages/Home';
import { canAccessRoute } from './utils/navigationUtils';

// Redirect component for /property/:id to /proprieta/:id
const PropertyRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/proprieta/${id}`} replace />;
};

const PrivateRoute: React.FC<{ children: React.ReactNode }> = React.memo(({ children }) => {
  const { token, profileLoading } = useAuth();
  if (!token) {
    // Se non loggato, vai subito alla login
    return <Navigate to="/login" replace />;
  }
  if (profileLoading) {
    return <LoadingSpinner />;
  }
  return <>{children}</>;
});

function SessionGuard() {
  const { user, token } = useAuth();
  
  return (
    <NotificationProvider userId={user?.id ? parseInt(user.id) : undefined} token={token}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <SessionGuardContent />
      </Router>
    </NotificationProvider>
  );
}

function SessionGuardContent() {
  let content;
  const { logout, user, token } = useAuth();
  const inactivityTimer = useRef<NodeJS.Timeout | null>(null);
  const location = useLocation();
  const isResizing = useRef(false);

  // Recupera ruolo utente una sola volta - ottimizzato con useMemo
  const userRole = React.useMemo(() => user?.role, [user?.role]);

  // Funzione di autorizzazione ottimizzata con useCallback
  const canAccess = React.useCallback((role: string | undefined, path: string): boolean => {
    return canAccessRoute(role, path);
  }, []);

  // Ottimizza il timer di inattività con useCallback
  const resetTimer = React.useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      try {
        localStorage.setItem('sessionExpired', 'true');
      } catch {
        // Ignore localStorage errors in incognito mode
      }
      logout();
    }, 30 * 60 * 1000); // 30 minuti
  }, [logout]);

  useEffect(() => {
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];

    // Throttle degli eventi per ridurre il numero di chiamate
    let throttleTimer: NodeJS.Timeout | null = null;
    const throttledReset = () => {
      if (!throttleTimer && !isResizing.current) {
        throttleTimer = setTimeout(() => {
          resetTimer();
          throttleTimer = null;
        }, 100); // Throttle a 100ms
      }
    };

    // Listener per resize
    const handleResizeStart = () => {
      isResizing.current = true;
    };
    const handleResizeEnd = () => {
      isResizing.current = false;
    };

    window.addEventListener('resize', handleResizeStart);
    let resizeTimer: NodeJS.Timeout;
    const throttledResizeEnd = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(handleResizeEnd, 150); // Dopo 150ms dal resize
    };
    window.addEventListener('resize', throttledResizeEnd);

    events.forEach((event) => window.addEventListener(event, throttledReset, { passive: true }));
    resetTimer();

    return () => {
      events.forEach((event) => window.removeEventListener(event, throttledReset));
      window.removeEventListener('resize', handleResizeStart);
      window.removeEventListener('resize', throttledResizeEnd);
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      if (throttleTimer) clearTimeout(throttleTimer);
      clearTimeout(resizeTimer);
    };
  }, [resetTimer]);

  content = user && user.id ? (
      <RoutePreloader>
        <AdaptiveLayout>
          <Suspense fallback={<LoadingSpinner />}>
          <ErrorBoundary>
          <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/login/finalize" element={<LoginFinalizePage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify" element={<EmailVerificationPage />} />
              <Route path="/test" element={<div>Test page - Server funziona!</div>} />
              {/* Rotte visibili solo agli admin */}
              <Route path="/admin" element={
                <PrivateRoute>
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                </PrivateRoute>
              } />
              <Route path="/admin/documents" element={
                <PrivateRoute>
                  <AdminRoute>
                    <DocumentVerificationPage />
                  </AdminRoute>
                </PrivateRoute>
              } />
              <Route path="/admin/audit-log" element={
                <PrivateRoute>
                  <AdminRoute>
                    <AuditLogPage />
                  </AdminRoute>
                </PrivateRoute>
              } />
              {/* Landing: admin → /admin, agency → /landlord, tenant → /dashboard, altri → Home */}
              <Route path="/" element={<Home />} />
              {/* Route pubbliche per documenti legali */}
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

              {/* Cruscotto Premium unificato per tutti i ruoli autenticati */}
              <Route path="/cruscotto" element={
                <PrivateRoute>
                  {canAccess(userRole, '/cruscotto') ? <CruscottoPremium /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />

              {/* Candidature - Sistema Italiano Unificato */}
              <Route path="/candidature" element={
                <PrivateRoute>
                  {canAccess(userRole, '/candidature') ? <RentalApplications /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />
              
              {/* Redirect vecchia route inglese */}
              <Route path="/applications" element={<Navigate to="/candidature" replace />} />

              {/* Contratti - Sistema Italiano Unificato */}
              <Route path="/contracts" element={
                <PrivateRoute>
                  {canAccess(userRole, '/contracts') ? <Contracts /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />

              {/* Dettagli Contratto */}
              <Route path="/contracts/:leaseId/details" element={
                <PrivateRoute>
                  {canAccess(userRole, '/contracts') ? <ContractDetailPage /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />

              {/* Storico Pagamenti */}
              <Route path="/contracts/:leaseId/payments" element={
                <PrivateRoute>
                  {canAccess(userRole, '/contracts') ? <PaymentHistoryPage /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />

              {/* Nuova Richiesta Manutenzione */}
              <Route path="/contracts/:leaseId/maintenance/new" element={
                <PrivateRoute>
                  {canAccess(userRole, '/contracts') ? <NewMaintenanceRequestPage /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />

              {/* Documenti Contratto */}
              <Route path="/contracts/:leaseId/documents" element={
                <PrivateRoute>
                  {canAccess(userRole, '/contracts') ? <ContractDocumentsPage /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />

              {/* Recensioni - Sistema Italiano Unificato */}
              <Route path="/recensioni" element={
                <PrivateRoute>
                  {canAccess(userRole, '/recensioni') ? <ReviewsPage /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />
              
              {/* Redirect vecchie route inglesi */}
              <Route path="/reviews" element={<Navigate to="/recensioni" replace />} />
              <Route path="/landlord-reviews" element={<Navigate to="/recensioni" replace />} />
              <Route path="/tenant-reviews" element={<Navigate to="/recensioni" replace />} />
              <Route path="/tenant-search" element={
                <PrivateRoute>
                  {canAccess(userRole, '/tenant-search') ? <TenantSearch /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />
              <Route path="/landlord-search" element={
                <PrivateRoute>
                  {canAccess(userRole, '/landlord-search') ? <LandlordSearch /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />
              <Route path="/search" element={
                <PrivateRoute>
                  <Navigate to="/" replace />
                </PrivateRoute>
              } />
              {/* Consents, documents, profile: accessibili a tutti i ruoli previsti */}
              <Route path="/consents" element={
                <PrivateRoute>
                  {canAccess(userRole, '/consents') ? <ConsentsPage /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />
              <Route path="/documents" element={
                <PrivateRoute>
                  {canAccess(userRole, '/documents') ? <DocumentsPage /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />
              <Route path="/profile" element={
                <PrivateRoute>
                  {canAccess(userRole, '/profile') ? <ProfilePage /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />
              
              {/* Notifiche - accessibile a tutti gli utenti autenticati */}
              <Route path="/notifiche" element={
                <PrivateRoute>
                  {canAccess(userRole, '/notifiche') ? <NotificationsPage /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />

              {/* Messaggi - accessibile a tutti gli utenti autenticati */}
              <Route path="/messaggi" element={
                <PrivateRoute>
                  {canAccess(userRole, '/messaggi') ? <MessagesPage /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />

              {/* Assistenza - accessibile a tutti gli utenti autenticati */}
              <Route path="/assistenza" element={
                <PrivateRoute>
                  {canAccess(userRole, '/assistenza') ? <SupportPage /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />
              <Route path="/support" element={<Navigate to="/assistenza" />} />

              {/* Redirect da /properties a /proprieta */}
              <Route path="/properties" element={<Navigate to="/proprieta" replace />} />
              
              {/* Redirect da /property/:id a /proprieta/:id */}
              <Route path="/property/:id" element={<PropertyRedirect />} />

              {/* Elenco Proprietà - landlord, agency, admin */}
              <Route path="/proprieta" element={
                <PrivateRoute>
                  {canAccess(userRole, '/proprieta') ? <LandlordDashboard /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />

              {/* Nuova Proprietà - landlord, admin */}
              <Route path="/proprieta/nuova" element={
                <PrivateRoute>
                  {canAccess(userRole, '/proprieta/nuova') ? <PropertyFormPage /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />

              {/* Modifica Proprietà - landlord, admin */}
              <Route path="/proprieta/:id/modifica" element={
                <PrivateRoute>
                  {canAccess(userRole, '/proprieta') ? <PropertyFormPage /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />

              {/* Dettaglio Proprietà - accessibile a tutti */}
              <Route path="/proprieta/:id" element={<PropertyDetailPage />} />

              {/* Dettaglio Candidatura - con controllo RBAC */}
              <Route path="/candidature/:id" element={
                <PrivateRoute>
                  {canAccess(userRole, '/candidature') ? <ApplicationDetailPage /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />

              {/* Nuova recensione - con controllo RBAC */}
              <Route path="/recensioni/nuova" element={
                <PrivateRoute>
                  {canAccess(userRole, '/recensioni') ? <ReviewFormPage /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />

              {/* Modifica recensione - con controllo RBAC */}
              <Route path="/recensioni/:id/modifica" element={
                <PrivateRoute>
                  {canAccess(userRole, '/recensioni') ? <ReviewFormPage /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />

              {/* Dettaglio singola recensione - con controllo RBAC */}
              <Route path="/recensioni/:id" element={
                <PrivateRoute>
                  {canAccess(userRole, '/recensioni') ? <ReviewFormPage /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />

              {/* KPI Ranking - admin, agency, landlord */}
              <Route path="/kpi-ranking" element={
                <PrivateRoute>
                  {canAccess(userRole, '/kpi-ranking') ? <RankingKpiCenter /> : <Navigate to="/" replace />}
                </PrivateRoute>
              } />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </ErrorBoundary>
          </Suspense>
        </AdaptiveLayout>
        </RoutePreloader>
    ) : (
      <RoutePreloader>
        <AdaptiveLayout>
          <Suspense fallback={<LoadingSpinner />}>
          <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login/finalize" element={<LoginFinalizePage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify" element={<EmailVerificationPage />} />
            <Route path="/test" element={<div>Test page - Server funziona!</div>} />
            {/* Landing: admin → /admin, agency → /landlord, tenant → /dashboard, altri → Home */}
            <Route path="/" element={<Home />} />
            {/* Route pubbliche per documenti legali */}
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/agency-landing" element={<AgencyLanding />} />
            <Route path="/search" element={<Navigate to="/" replace />} />
            <Route path="/tenant-search" element={<TenantSearch />} />
            <Route path="/landlord-search" element={<LandlordSearch />} />
            <Route path="/assistenza" element={<SupportPage />} />
            <Route path="/support" element={<Navigate to="/assistenza" />} />
            {/* Dettaglio Proprietà pubblico */}
            <Route path="/proprieta/:id" element={<PropertyDetailPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </ErrorBoundary>
          </Suspense>
        </AdaptiveLayout>
        </RoutePreloader>
    );
    if (!content) content = <LoadingSpinner />;
    return (
      <>
        {content}
        {/* Cookie Banner - mostrato su tutte le pagine */}
        <CookieBanner />
      </>
    );
}

export default SessionGuard;
